import type { Invoice } from '@/lib/api/invoices';

export const DEFAULT_INVOICE_CURRENCY = 'USD';

const INVOICE_CURRENCY_SYMBOL_OVERRIDES: Record<string, string> = {
  AUD: '$',
  CAD: '$',
  EGP: '£',
  HKD: '$',
  NZD: '$',
  SGD: '$',
  USD: '$',
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

export const normalizeInvoiceCurrency = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  return normalized || null;
};

export const formatInvoiceCurrencyAmount = (
  value: number | string | null | undefined,
  currency?: unknown
): string => {
  const rawAmount = Number(value);
  const amount = Number.isFinite(rawAmount) ? rawAmount : 0;
  const normalizedCurrency = normalizeInvoiceCurrency(currency) || DEFAULT_INVOICE_CURRENCY;
  const overrideSymbol = INVOICE_CURRENCY_SYMBOL_OVERRIDES[normalizedCurrency];

  if (overrideSymbol) {
    return `${overrideSymbol}${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalizedCurrency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    const prefix = getInvoiceCurrencyBadgeLabel(normalizedCurrency);

    return `${prefix}${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
};

export const getInvoiceCurrency = (
  invoice?: Pick<Invoice, 'currency'> | null
): string | null => normalizeInvoiceCurrency(invoice?.currency);

export const getInvoiceListDisplayCurrency = (
  invoices: Array<Pick<Invoice, 'currency'>>,
  fallback?: unknown
): string => {
  const invoiceCurrency = invoices
    .map((invoice) => getInvoiceCurrency(invoice))
    .find((currency): currency is string => Boolean(currency));

  return invoiceCurrency || normalizeInvoiceCurrency(fallback) || DEFAULT_INVOICE_CURRENCY;
};

export const getInvoiceCurrencyBadgeLabel = (currency?: unknown): string => {
  const normalizedCurrency = normalizeInvoiceCurrency(currency) || DEFAULT_INVOICE_CURRENCY;
  const overrideSymbol = INVOICE_CURRENCY_SYMBOL_OVERRIDES[normalizedCurrency];

  if (overrideSymbol) {
    return overrideSymbol;
  }

  try {
    const currencyPart = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalizedCurrency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value;

    return currencyPart || normalizedCurrency;
  } catch {
    return normalizedCurrency;
  }
};

export const readInvoiceCurrencyFromResponse = (response: unknown): string | null => {
  const record = asRecord(response);
  const summary = asRecord(record.summary);

  return (
    normalizeInvoiceCurrency(summary.currency) ||
    normalizeInvoiceCurrency(summary.invoiceCurrency) ||
    normalizeInvoiceCurrency(record.currency) ||
    normalizeInvoiceCurrency(record.invoiceCurrency) ||
    normalizeInvoiceCurrency(record.nutritionistCurrency)
  );
};
